import { useCallback, useState } from 'react';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { EditorSavingStateEnum, dukeTTEditorState } from './dukeTTEditorState';

/**
 * Recovery action for a stale editor: reload the page to pull the latest
 * content. The editors seed their content once on mount, so a full reload is
 * the reliable way to re-hydrate them and reset the tracked revision, and it
 * matches what this is, discarding a stale view rather than merging.
 *
 * Returns a handler to trigger the reload and the confirmation dialog to render.
 * When the tab is clean the reload happens immediately; when it has unsaved
 * edits the handler opens a confirm dialog first, since reloading would discard
 * them (live merge is a separate, larger piece of work). `entityLabel` names the
 * thing being edited so the confirm reads naturally in each editor.
 */
export function useLoadLatest(entityLabel: string) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  const requestLoadLatest = useCallback(() => {
    if (dukeTTEditorState.state === EditorSavingStateEnum.UNSAVED_CHANGES) {
      setIsConfirmOpen(true);
      return;
    }
    reload();
  }, [reload]);

  const confirmDialog = (
    <ConfirmationDialog
      isOpen={isConfirmOpen}
      onClose={() => setIsConfirmOpen(false)}
      variant="destructive"
      title="Discard your changes?"
      body={`Loading the latest version will discard your recent edits to this ${entityLabel}.`}
      confirmLabel="Discard and load latest"
      onConfirm={reload}
    />
  );

  return { requestLoadLatest, confirmDialog };
}
